/**
 * React Query Client Configuration
 * 
 * Provides client-side caching for GraphQL API calls to improve performance.
 * - Deduplicates concurrent requests
 * - Caches responses with configurable stale times
 * - Automatically refetches stale data in background
 */

import { QueryClient } from '@tanstack/react-query';

/**
 * Invalidate every myTournaments cache variant (Home uses upcomingOnly: false,
 * LiveSessionBar uses { limit: 50 }, etc.). Status transitions after the final
 * match / finalize must drop stale "live" rows immediately.
 */
export function invalidateMyTournamentsCaches(client: QueryClient = queryClient) {
  return client.invalidateQueries({ queryKey: ['myTournaments'] });
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Data is considered fresh for 30 seconds
      staleTime: 30 * 1000,
      // Keep unused data in cache for 5 minutes
      gcTime: 5 * 60 * 1000,
      // Retry failed requests once
      retry: 1,
      // Don't refetch on window focus by default (can be noisy)
      refetchOnWindowFocus: false,
    },
  },
});

// Query keys for consistent cache management
export const queryKeys = {
  // Club queries
  myClubs: ['myClubs'] as const,
  clubTournaments: (clubId: string) => ['clubTournaments', clubId] as const,
  
  // Club discovery queries
  clubs: {
    list: (options?: { sortBy?: string; limit?: number }) => ['clubs', 'list', options] as const,
    search: (query: string, options?: { limit?: number }) => ['clubs', 'search', query, options] as const,
    bySlug: (slug: string) => ['clubs', 'bySlug', slug] as const,
    byInviteCode: (code: string) => ['clubs', 'byInviteCode', code.toUpperCase()] as const,
    tournaments: (clubId: string, limit?: number) => ['clubs', 'tournaments', clubId, limit] as const,
  },
  
  // Tournament queries
  myTournaments: (filters?: { status?: string[]; clubId?: string; upcomingOnly?: boolean }) => 
    ['myTournaments', filters] as const,
  tournament: (id: string) => ['tournament', id] as const,
  publicTournament: (slug: string) => ['publicTournament', slug] as const,
  tournamentSummary: (slug: string) => ['tournamentSummary', slug] as const,
  
  // Late player registration queries (Feature: late-player-registration)
  // Requirements: 1.4, 11.2
  lateRegistration: {
    matchProgress: (tournamentId: string) => 
      ['lateRegistration', 'matchProgress', tournamentId] as const,
    lateArrivals: (tournamentId: string) => 
      ['lateRegistration', 'lateArrivals', tournamentId] as const,
  },
  
  // Notification queries
  notifications: (filters?: { unreadOnly?: boolean; limit?: number }) => 
    ['notifications', filters] as const,
  
  // Profile queries
  profile: ['profile'] as const,

  // Player Bey Box queries (beyblade-combo-tracking)
  myBeys: ['myBeys'] as const,

  // Deck registration queries (beyblade-combo-tracking) — keyed by tournament.
  myDeck: (tournamentId: string) => ['myDeck', tournamentId] as const,

  // TD deck visibility (beyblade-combo-tracking, task 8.1) — keyed by tournament.
  tournamentDecks: (tournamentId: string) => ['tournamentDecks', tournamentId] as const,

  // Tournament judges (tournament-judges) — the TD's list, and the caller's own
  // assignment (which every signed-in viewer asks for, judge or not).
  tournamentJudges: (tournamentId: string) => ['tournamentJudges', tournamentId] as const,
  myJudgeAssignment: (tournamentId: string) => ['myJudgeAssignment', tournamentId] as const,
  
  // Walk-in registration queries
  // Stale time configuration (configured per-query in hooks):
  // - searchPlayers: staleTime: 10s (short - data changes during check-in)
  // - recentPlayers: staleTime: 60s (medium - updated after each registration)
  // - clubProfiles: staleTime: 5min (longer - rarely changes during event)
  walkIn: {
    search: (clubId: string, tournamentId: string, query: string) => 
      ['walkIn', 'search', clubId, tournamentId, query] as const,
    recentPlayers: (clubId: string, tournamentId: string) => 
      ['walkIn', 'recentPlayers', clubId, tournamentId] as const,
    clubProfiles: (clubId: string) => 
      ['walkIn', 'clubProfiles', clubId] as const,
  },
};
